/**
 * CustomerRatesDialog — set a customer's price without leaving the In Review ticket
 * (task #33; gap 1 D-14 / D-20, DEC-383).
 *
 * Two modes:
 *   - JOB_TYPE (the default for D-20 roles): "Price for <JOB TYPE> for this customer". One price
 *     in the job type's unit, POST /v1/inreview/:jobId/job-type-price. The SERVER starts it on the
 *     ticket's billing day (the body has no date), so this ticket and the customer's other
 *     unapproved tickets of this job type pick it up. Corrections live in Billing → Customer
 *     prices.
 *   - GENERAL: the customer's general per-method rates, the same fields, rules
 *     (customer-rates.tsx) and PATCH /v1/hauler-operators/:id the full customer form uses, so the
 *     same permission (MANAGE_HAULER_CATALOG) and the same audit trail.
 *
 * The customer is fetched ONLY when the general form is allowed (B1): GET /v1/hauler-operators/:id
 * needs VIEW_HAULER_CATALOG, which a billing manager lacks. In JOB_TYPE mode the name comes from
 * the ticket (`customerName`). On save both modes refetch the In Review queries, so the ticket's
 * Effective Rate and banner update in place.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@lodex-pro/web-shared';
import { hasSubCentPrecision, parseDollarsToCents } from '@lodex-pro/shared/money';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { fieldStyle, FormRow, labelStyle } from '../sites-operators/form-bits';
import {
  CustomerRatesFields,
  EMPTY_RATES,
  parseRatesDraft,
  ratesDraftFrom,
  type RatesDraft,
} from '../sites-operators/customer-rates';
import { useHaulerOperator, useUpdateHaulerOperator } from '../sites-operators/hooks';
import { useSetJobTypePriceFromReview } from './use-inreview';

export interface CustomerRatesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly haulerOperatorId: string;
  /** The ticket's customer label (t.site.operatorName); the JOB_TYPE title never fetches it. */
  readonly customerName: string | null;
  readonly jobId: string;
  /** The ticket's pinned job type: its family (the price key), name and unit. */
  readonly jobType: {
    readonly familyId: string;
    readonly label: string;
    readonly billingMethod: string;
  };
  /** The ticket's billing day ('YYYY-MM-DD'); the server starts the job-type price on it. */
  readonly priceDay: string | null;
  /** D-20 — MANAGE_CUSTOMER_PRICES and REVIEW_TICKET (JOB_TYPE_PRICE_FIX_ROLES). */
  readonly allowJobType: boolean;
  /** MANAGE_ROLES — may edit the customer's general rates (and read the customer). */
  readonly allowGeneral: boolean;
}

type Mode = 'JOB_TYPE' | 'GENERAL';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' -> 'Jan 2, 2027' (a calendar day, no time zone); anything else verbatim. */
export function formatDay(day: string | null): string {
  if (day === null) return 'not known';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const month = m === null ? undefined : MONTHS[Number(m[2]) - 1];
  if (m === null || month === undefined) return day;
  return `${month} ${String(Number(m[3]))}, ${m[1]}`;
}

/** The job type's unit, as the price box label. */
function unitLabel(billingMethod: string): string {
  switch (billingMethod) {
    case 'PER_BBL':
      return '$ per bbl';
    case 'HOURLY':
      return '$ per hour';
    case 'PER_LOAD':
      return '$ per load';
    default:
      return '$';
  }
}

export const JOB_TYPE_PRICE_INVALID = 'Enter a dollar amount, e.g. 150.00. $0.00 is allowed.';
export const JOB_TYPE_PRICE_SUB_CENT = 'Use dollars and cents only, e.g. 1.25 (not 1.255).';

/** One price box -> cents. $0 is a deliberate free price (D-09); blank, negative or junk is refused. */
function parsePrice(
  raw: string,
): { readonly ok: true; readonly cents: number } | { readonly ok: false; readonly error: string } {
  if (hasSubCentPrecision(raw)) return { ok: false, error: JOB_TYPE_PRICE_SUB_CENT };
  const parsed = parseDollarsToCents(raw);
  if (!parsed.ok || parsed.cents < 0) return { ok: false, error: JOB_TYPE_PRICE_INVALID };
  return { ok: true, cents: parsed.cents };
}

/** The API's own refusal sentence (error.fields[0].reason), else a generic retry line. */
function saveErrorText(err: unknown): string {
  if (err instanceof ApiError && err.fields.length > 0) {
    const reason = err.fields[0]?.reason;
    if (reason !== undefined && reason !== '') return reason;
  }
  return 'Could not save. Check your inputs and try again.';
}

const errorStyle: React.CSSProperties = { ...labelStyle, color: 'var(--color-danger)' };

export function CustomerRatesDialog({
  open,
  onOpenChange,
  haulerOperatorId,
  customerName,
  jobId,
  jobType,
  priceDay,
  allowJobType,
  allowGeneral,
}: CustomerRatesDialogProps): React.JSX.Element {
  const qc = useQueryClient();
  // B1 — only a user who may edit the general rates reads the customer.
  const { data, isLoading, isError } = useHaulerOperator(
    open && allowGeneral ? haulerOperatorId : null,
  );
  const customer = data?.haulerOperator;
  const update = useUpdateHaulerOperator(haulerOperatorId);
  const setPrice = useSetJobTypePriceFromReview(jobId);

  const [mode, setMode] = React.useState<Mode>(allowJobType ? 'JOB_TYPE' : 'GENERAL');
  const [rates, setRates] = React.useState<RatesDraft>(EMPTY_RATES);
  const [price, setPriceText] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Seed from the server each time the dialog opens on a loaded customer.
  React.useEffect(() => {
    if (!open || customer === undefined) return;
    setRates(ratesDraftFrom(customer));
    setError(null);
  }, [open, customer]);

  const close = (): void => onOpenChange(false);
  const pick = (next: Mode): void => {
    setMode(next);
    setError(null);
  };

  const saveGeneral = (): void => {
    const parsed = parseRatesDraft(rates);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    // Only the pricing fields: the PATCH is partial, so nothing else on the customer moves.
    update.mutate(parsed.rates, {
      onSuccess: () => {
        // The ticket's billing block is the server's own assessment — refetch it
        // rather than recompute here. ['inreview'] covers the open ticket and the queue.
        void qc.invalidateQueries({ queryKey: ['inreview'] });
        toast.success('Customer rates saved');
        close();
      },
      onError: () => setError('Could not save. Check your inputs and try again.'),
    });
  };

  const saveJobType = (): void => {
    const parsed = parsePrice(price);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    const note = notes.trim();
    // No date: the server starts the price on this ticket's billing day (D-14).
    setPrice.mutate(
      { rateCents: parsed.cents, notes: note === '' ? null : note },
      {
        onSuccess: () => {
          toast.success('Job-type price saved');
          close();
        },
        onError: (err: unknown) => setError(saveErrorText(err)),
      },
    );
  };

  const jobTypeMode = mode === 'JOB_TYPE';
  const name = jobTypeMode
    ? (customerName ?? 'this customer')
    : (customer?.resolvedName ?? customerName ?? 'this customer');
  const saving = jobTypeMode ? setPrice.isPending : update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-rates-dialog">
        <DialogHeader>
          <DialogTitle>Set this customer&apos;s price</DialogTitle>
          <DialogDescription>
            <span data-testid="customer-rates-customer" style={{ fontWeight: 600 }}>
              {name}
            </span>
            {'. '}
            {jobTypeMode
              ? `Starts on this ticket's billing day (${formatDay(priceDay)}), so this ticket and the customer's other tickets of this job type from that day pick it up. Change it later in Billing → Customer prices.`
              : 'Saved to the customer for every job type without its own price. This ticket updates when you save.'}
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {allowJobType && allowGeneral && (
            <div
              role="radiogroup"
              aria-label="What to set"
              data-testid="customer-rates-mode"
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <label style={{ ...labelStyle, textTransform: 'none', marginBottom: 0 }}>
                <input
                  type="radio"
                  name="ir-rates-mode"
                  checked={jobTypeMode}
                  onChange={(): void => pick('JOB_TYPE')}
                />{' '}
                Price for {jobType.label} for this customer
              </label>
              <label style={{ ...labelStyle, textTransform: 'none', marginBottom: 0 }}>
                <input
                  type="radio"
                  name="ir-rates-mode"
                  checked={!jobTypeMode}
                  onChange={(): void => pick('GENERAL')}
                />{' '}
                This customer&apos;s general rate
              </label>
            </div>
          )}

          {jobTypeMode ? (
            <>
              <FormRow
                label={`Price for ${jobType.label} (${unitLabel(jobType.billingMethod)})`}
                htmlFor="ir-job-type-price"
              >
                <input
                  id="ir-job-type-price"
                  inputMode="decimal"
                  value={price}
                  onChange={(e): void => setPriceText(e.target.value)}
                  placeholder="150.00"
                  aria-invalid={error !== null}
                  style={{ ...fieldStyle, maxWidth: '140px' }}
                />
              </FormRow>
              {error !== null && (
                <p role="alert" data-testid="job-type-price-error" style={errorStyle}>
                  {error}
                </p>
              )}
              <FormRow label="Notes (optional)" htmlFor="ir-job-type-notes">
                <textarea
                  id="ir-job-type-notes"
                  value={notes}
                  maxLength={2000}
                  onChange={(e): void => setNotes(e.target.value)}
                  rows={2}
                  style={{ ...fieldStyle, resize: 'vertical' }}
                />
              </FormRow>
            </>
          ) : (
            <>
              {isLoading && <p style={{ ...labelStyle, textTransform: 'none' }}>Loading…</p>}
              {isError && (
                <p role="alert" style={errorStyle}>
                  Could not load this customer.
                </p>
              )}
              {customer !== undefined && (
                <CustomerRatesFields value={rates} onChange={setRates} idPrefix="ir-rates" />
              )}
              {error !== null && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
            </>
          )}

          {allowGeneral && (
            <Link
              to={`/sites-operators?tab=operators&operatorId=${encodeURIComponent(haulerOperatorId)}`}
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-accent-blue)' }}
            >
              Open the full customer screen →
            </Link>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="customer-rates-save"
            disabled={saving || (!jobTypeMode && customer === undefined)}
            onClick={jobTypeMode ? saveJobType : saveGeneral}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {saving ? 'Saving…' : jobTypeMode ? 'Save price' : 'Save rates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
